/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Box, Grid, Stack } from "@mui/material";
import FormField from "components/generals/FormInput";
import FormikForm from "components/layout/FormikForm";
import { Form, FormikProps } from "formik";
import { autoTouchErrorFields, checkFormValidity, DoctorFormYUPValidation } from "utils/form.utils";
import {
  ProviderInterface,
  HeaderEnum,
  AssetInterface,
  MediaSourceEnum,
  MediaCategoryEnum,
  MediaStatusEnum
} from "@malmur-technologies/medicalsystem-shared";
import useHttp from "hooks/http.hook";
import { FormProps } from "interfaces";
import StepperFormActionButtons from "../custom/StepperFormActionButtons";
import Typography from "components/generals/Typography";
import BadgeIcon from "@mui/icons-material/Badge";
import { useTranslation } from "react-i18next";
import FormInputPassword from "components/generals/FormInputPassword";
import { FormActionType } from "types/form.enum";
import { Method } from "axios";
import { CoreAPI_URLs } from "constants/http.const";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectGeneralSettings } from "store/slices/generalSettingsSlice";
import { CountryEnum } from "@malmur-technologies/medicalsystem-shared";
import InputFileUpload from "components/generals/InputFileUpload";
import { Dispatch, SetStateAction } from "react";
import Avatar from "components/generals/Avatar";
import { useAuthenticatedAsset } from "hooks/useAuthenticatedAsset.hook";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { EntityPermissions, PermissionsUser } from "types";
/**
 * Props for the DoctorForm component.
 * @interface DoctorFormProps
 * @extends {FormProps<ProviderInterface>}
 * @property {boolean} [isDoctor] - Flag indicating if the provider is a doctor (vs nurse)
 * @property {(updatedModel: Partial<ProviderInterface>) => void} [updateListHandler] - Callback to handle provider updates
 * @property {Dispatch<SetStateAction<File | null>>} [setProviderAssetImage] - State setter for provider asset image to be uploaded after creation
 */
interface DoctorFormProps extends FormProps<ProviderInterface> {
  isDoctor?: boolean;
  updateListHandler?: (updatedModel: Partial<ProviderInterface>) => void;
  setProviderAssetImage?: Dispatch<SetStateAction<File | null>>;
}

/**
 * Form component for doctor/provider professional credentials and license information.
 * Handles medical license, specialty, DEA registration, and NPI number.
 * Can be used standalone or as part of a stepper workflow.
 * 
 * Features:
 * - Medical license number input
 * - Specialty field
 * - DEA registration (optional, removed if empty)
 * - NPI (National Provider Identifier) number
 * - Password field for new providers
 * - Supports both doctor and nurse modes
 * - Form validation with Yup schema
 * - Integration with stepper for multi-step forms
 * - API integration for create/update operations
 * 
 * Use Cases:
 * - Creating new doctor/provider accounts
 * - Updating existing provider credentials
 * - Part of multi-step provider registration
 * 
 * @param {DoctorFormProps} props - Component props
 * @returns {React.ReactElement} The doctor credentials form
 */
const DoctorForm: React.FC<DoctorFormProps> = ({
  model,
  isDoctor,
  action,
  closeAction,
  nextStep,
  backStepHandler,
  lastStep,
  firstStep,
  setProviderAssetImage,
  ...props
}) => {
  const [isLoading, _error, sendRequest] = useHttp();
  const generalSettings = useSelector(selectGeneralSettings);
  const { t } = useTranslation();
  const [newImage, setNewImage] = useState<File | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [existingAsset, setExistingAsset] = useState<AssetInterface | null>(null);
  const titleData = isDoctor ? t("doctorForm.doctorData") : t("doctorForm.nurseData");
  const hasMarkedFieldsRef = useState<{ current: boolean }>({ current: false })[0];

  // Use authenticated asset hook for existing provider image
  const { blobUrl: currentPhotoUrl } = useAuthenticatedAsset(existingAsset || undefined);

  // Fetch existing provider asset image when editing an existing doctor
  useEffect(() => {
    if (model?.id && isDoctor) {
      sendRequest(
        CoreAPI_URLs.PROVIDER_ASSET_RELATION,
        (relations: Array<{ asset: AssetInterface }>) => {
          if (relations && relations.length > 0 && relations[0].asset) {
            setExistingAsset(relations[0].asset);
          }
        },
        "GET",
        undefined,
        [{ header: HeaderEnum.RESOURCE_ID, value: model.id }]
      );
    }
  }, [model?.id, isDoctor]);

  const submitHandler = (model: Partial<ProviderInterface>) => {
    if (model.deaRegistration == "") {
      model.deaRegistration = undefined;
    }

    if (nextStep) {
      nextStep(model);
    } else {
      const method: Method = action === FormActionType.NEW ? "POST" : "PUT";
      const successMessageText = action === FormActionType.NEW ? t("doctorForm.providerCreated") : t("doctorForm.providerUpdated");
      sendRequest(
        CoreAPI_URLs.PROVIDER_GENERAL_OPERATIONS,
        onDoctorUpdateHandler,
        method,
        JSON.stringify(model),
        [{ header: HeaderEnum.RESOURCE_ID, value: model.id }],
        { successMessage: successMessageText }
      );
    }
  };

  const onDoctorUpdateHandler = async (doctor: ProviderInterface) => {
    if (action === FormActionType.MODIFY) {
      props.setModelList(props.modelList.map((model) => (model.id === doctor.id ? doctor : model)));

      if (newImage && doctor.id) {
        // First, delete existing provider-asset relation if it exists
        await sendRequest(
          CoreAPI_URLs.PROVIDER_ASSET_RELATION,
          null,
          "DELETE",
          undefined,
          [{ header: HeaderEnum.RESOURCE_ID, value: doctor.id }]
        );

        // Then create the new asset and relation
        const assetData = {
          mimetype: newImage.type,
          source: MediaSourceEnum.OTHER,
          category: MediaCategoryEnum.PROFILE,
          orgName: newImage.name,
          status: MediaStatusEnum.PENDING,
        };

        await sendRequest<AssetInterface>(
          CoreAPI_URLs.ASSETS,
          async (responseAsset) => {
            if (responseAsset) {
              const formData = new FormData();
              formData.append("file", newImage);
              await sendRequest<AssetInterface>(
                CoreAPI_URLs.ASSETS_UPLOAD,
                async (responseAssetUpload) => {
                  if (responseAssetUpload) {
                    const providerAssetRelation = {
                      provider: { id: doctor.id },
                      asset: { id: responseAssetUpload.id },
                    };
                    await sendRequest(
                      CoreAPI_URLs.PROVIDER_ASSET_RELATION,
                      null,
                      "POST",
                      JSON.stringify(providerAssetRelation)
                    );
                  }
                },
                "POST",
                formData,
                [{ header: HeaderEnum.RESOURCE_ID, value: responseAsset.id }]
              );
            }
          },
          "POST",
          JSON.stringify(assetData)
        );
      }
    } else {
      props.setModelList((oldArray) => [...oldArray, doctor]);
    }
  };

  const handleImageChange = (file: File | null) => {
    setNewImage(file);
    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
    }
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setPreviewImageUrl(imageUrl);
    } else {
      setPreviewImageUrl(null);
    }
    // Propagate the image to the parent component (StepperForm) for later upload
    if (setProviderAssetImage) {
      setProviderAssetImage(file);
    }
  };



  return (
    <FormikForm
      onSubmit={(model) => {
        submitHandler(model);
      }}
      initialValues={model}
      validationSchema={DoctorFormYUPValidation(t, generalSettings?.country)}
    >
      {({ values, errors, touched, setFieldTouched }: FormikProps<Partial<ProviderInterface>>) => {
        if (checkFormValidity(values, errors, touched) && !hasMarkedFieldsRef.current) {
          hasMarkedFieldsRef.current = true;
          setTimeout(() => {
            autoTouchErrorFields(values, errors, touched, setFieldTouched);
          }, 0);
        } else if (!checkFormValidity(values, errors, touched)) {
          hasMarkedFieldsRef.current = false;
        }
        return (
          <Form id="doctorForm" autoComplete="true">
            <Stack alignItems="center" direction="row" gap={2} mb={3}>
              <BadgeIcon fontSize="large" />
              <Typography variant="h4">{titleData}</Typography>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormInputPassword
                  label={t("doctorForm.medicalLicense")}
                  name="medicalLicense"
                  value={values.medicalLicense || ""}
                  error={errors.medicalLicense && touched.medicalLicense}
                  showModal={true}
                  modalBody={t("doctorForm.wouldShowML")}
                  modalTitle={t("doctorForm.showML")}
                  forceHidePassword={!!values.id}
                />
              </Grid>

              <Grid item xs={12} md={isDoctor ? 6 : 12} lg={isDoctor ? 6 : 12}>
                <FormField
                  type="Text"
                  label={t("doctorForm.speciality")}
                  name="specialty"
                  value={values.specialty || ""}
                  error={errors.specialty && touched.specialty}
                />
              </Grid>
              {isDoctor && (
                <>
                  <Grid item xs={12} md={6} lg={6}>
                    <FormInputPassword
                      label={generalSettings?.country === CountryEnum.MEXICO ? t("doctorForm.pgrRegistration") : t("doctorForm.deaRegistration")}
                      name="deaRegistration"
                      value={values.deaRegistration || ""}
                      error={errors.deaRegistration && touched.deaRegistration}
                      showModal={true}
                      modalBody={generalSettings?.country === CountryEnum.MEXICO ? t("doctorForm.wouldShowPgr") : t("doctorForm.wouldShowDea")}
                      modalTitle={generalSettings?.country === CountryEnum.MEXICO ? t("doctorForm.showPgr") : t("doctorForm.showDea")}
                      forceHidePassword={!!values.id}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    {(previewImageUrl || currentPhotoUrl) ?
                      (
                        <Avatar
                          alt={"Profile image"}
                          src={previewImageUrl || currentPhotoUrl}
                          className="docFormlogo"
                        />
                      ) : (null)
                    }
                    <PermissionWrapper requiredPermissions={[PermissionsUser[EntityPermissions.DOCTORS].CREATE]} >
                      <Box mb={2}>
                        <Typography variant="caption" mr={1}>{t("doctorForm.uploadPhoto")}</Typography>
                        <InputFileUpload
                          id="cy-doctor-logo"
                          accept="image/*"
                          setImage={handleImageChange}
                          addNewImage={firstStep !== undefined ? firstStep : true}
                        />
                      </Box>
                    </PermissionWrapper>
                  </Grid>
                </>
              )}
            </Grid>

            {checkFormValidity(values, errors, touched) && (
              <Box mt={2} mb={2}>
                <Typography
                  variant="body2"
                  className="docFormErrorMessage"
                >
                  {checkFormValidity(values, errors, touched)}
                </Typography>
              </Box>
            )}

            <StepperFormActionButtons
              backStepHandler={backStepHandler}
              nextStep={nextStep}
              firstStep={firstStep}
              lastStep={lastStep}
              isLoading={isLoading}
            />
          </Form>
        )
      }
      }
    </FormikForm>
  );
};

export default DoctorForm;